/**
 * The wildlife of Cwm Twrch — four animals, and four different problems
 * (M2-05, and the rabbit in M3-10).
 *
 * The numbers below are the whole of each creature's behaviour. There is no
 * wolf class and no boar subclass: `mind.ts` runs one state machine over these
 * records, and what makes a stag a stag is that it can see for fourteen tiles
 * and leaves at eight, while a boar can barely see six and *gets angrier when
 * you hurt it*.
 *
 * Read them as three answers to "how do I not get killed by this":
 *
 * - **Wolf** — you cannot avoid it, so you have to break its nerve. It probes,
 *   it takes a wound, it leaves. Its pack is the threat, and the pack breaks
 *   when two of them are down. Cooperation beats gear here as it does
 *   everywhere in M1-07.
 * - **Stag** — you cannot fight it, because you will never be allowed close
 *   enough. It has to be stalked downwind or hamstrung at range, which is what
 *   M2-03's aimed blows are ultimately *for*.
 * - **Boar** — you cannot break it, because it does not break. Wounding it
 *   makes it worse, its hide turns cuts, and the only things that work are the
 *   head and the legs. This is Ysgithyrwyn in miniature, and it is deliberately
 *   the least forgiving of the three.
 * - **Rabbit** (M3-10) — you can, actually. It is here because the other three
 *   are landmarks and a valley needs *background*: something living within a few
 *   minutes' walk of the village, cheap enough to practise stalking on and worth
 *   one portion of meat when you manage it.
 *
 * Three fields added in M3-10 are what turn all four from decorations into
 * inhabitants: `homeRange` (how far it wanders from where it was born),
 * `crepuscular` (whether dawn and dusk are its hours) and `browses` (whether it
 * eats the valley's plants). None of them is a behaviour — they are numbers the
 * one state machine in `beasts.ts` reads.
 */
import { createAttributes, DEFAULT_INSTINCT } from '@annwn/sim';
/* -------------------------------------------------------------------------- *
 * Instinct
 * -------------------------------------------------------------------------- */
/**
 * A wolf. Eyes forward, nose long, and no interest whatever in a fair fight.
 *
 * The nerve numbers are set against M2-05's first criterion directly: `nerve 3`
 * against `woundNerve 1.5` means two levels of wound take a lone wolf to zero
 * and it leaves — which is the "tests, bites, and disengages" line, stated as
 * the arithmetic that produces it rather than as a special case. `packNerve
 * 1.6` means one packmate down is survivable and two is not.
 */
export const WOLF_INSTINCT = {
    ...DEFAULT_INSTINCT,
    id: 'wolf',
    disposition: 'predator',
    senses: { sight: 10, arc: 2, smell: 6, upwindFactor: 2 },
    nerve: 3,
    woundNerve: 1.5,
    packNerve: 1.6,
    oddsNerve: 1,
    closeAt: 1,
    flightAt: 6,
    // A pack works a territory rather than a field. The widest range in the
    // valley, which is also what makes wolves the animal you meet on the way
    // somewhere rather than the animal you go to find.
    homeRange: 22,
    memory: 60,
};
/**
 * A red stag. Everything about it is distance.
 *
 * Sight of fourteen over a 180° arc — eyes on the sides of a prey animal's
 * head — and a nose that doubles upwind, so walking at one from the wrong
 * quarter announces you sixteen tiles out. It flees at eight, long before a
 * spear is any use, and it fights only when `cornered` leaves it nothing else.
 */
export const STAG_INSTINCT = {
    ...DEFAULT_INSTINCT,
    id: 'stag',
    disposition: 'prey',
    senses: { sight: 14, arc: 3, smell: 8, upwindFactor: 2 },
    nerve: 1,
    woundNerve: 2,
    packNerve: 0,
    oddsNerve: 1,
    closeAt: 1,
    flightAt: 8,
    // It ranges over a hillside, which is why a hunter who has found sign has
    // found a *place to look* rather than an animal.
    homeRange: 20,
    // Out at first and last light, lying up in the wood through the middle of the
    // day. This is the pairing M3-08 needs: the animal worth trailing is the
    // animal that has been moving, and it has been moving at dawn.
    crepuscular: true,
    browses: true,
    memory: 80,
};
/**
 * A wild boar. Short-sighted, long-nosed, and the `enraged` flag.
 *
 * It will ignore you at distance and commit absolutely inside two tiles, and
 * every wound you put in it makes it *harder* to drive off rather than easier.
 * A hunter's instinct — wear it down and it will break — is precisely the
 * instinct that gets people killed by boars, and the one flag below is the
 * whole of why.
 */
export const BOAR_INSTINCT = {
    ...DEFAULT_INSTINCT,
    id: 'boar',
    disposition: 'territorial',
    senses: { sight: 6, arc: 2, smell: 10, upwindFactor: 2 },
    nerve: 3,
    woundNerve: 1,
    packNerve: 0,
    oddsNerve: 0.5,
    enraged: true,
    closeAt: 2,
    flightAt: 8,
    // It keeps to its wallow and the wet ground around it. A boar you have found
    // once is a boar you can find again, which is what makes M5-04 a hunt for a
    // *particular* animal rather than a random encounter.
    homeRange: 12,
    // Rooting rather than grazing, and the same mechanic either way: it eats what
    // grows on the ground it is standing on.
    browses: true,
    memory: 100,
};
/**
 * Ysgithyrwyn, White-Tusk, Chief of Boars (M5-04) — the wild boar's instinct,
 * grown into a thing that hunts the hunters back.
 *
 * A boar's disposition and a boar's `enraged` flag, turned up in the two ways
 * that make him a boss rather than a big pig. His nerve is set so high that no
 * amount of odds or wounds will *break* it — a party cannot make him run by
 * frightening him, only by bleeding him — and `retreatBelowBlood` is the single
 * exception, the point at which his body overrules his temper and he makes for
 * the wallow. That pair is the whole two-act fight: press him and he only comes
 * on harder, until all at once he doesn't, and the hunt becomes a pursuit.
 *
 * His `homeRange` is tight and his `memory` long: he keeps to his bog until the
 * escalation (M5-04's scheduler) sends him out, and once he has seen you he does
 * not forget you for a very long time. A monster that knows you are coming.
 */
export const YSGITHYRWYN_INSTINCT = {
    ...DEFAULT_INSTINCT,
    id: 'ysgithyrwyn',
    disposition: 'territorial',
    // Small eyes, a nose that misses nothing, and a wide arc — you do not sneak up
    // on him the way you might a stag; downwind and patient is the only approach.
    senses: { sight: 6, arc: 2, smell: 12, upwindFactor: 2 },
    // Nerve nothing short of blood loss will break. Odds and wounds barely move it.
    nerve: 8,
    woundNerve: 1,
    packNerve: 0,
    oddsNerve: 0.3,
    enraged: true,
    // Below the `failing` blood mark (40) even his temper cannot keep him upright
    // and pressing; he turns for the wallow. This is the number the second act
    // hangs on, and M5-06's to move.
    retreatBelowBlood: 40,
    closeAt: 2,
    // Three seconds at each escalation beat: he lifts his head, holds the stare,
    // closes, then squares up before the first gore. Deliberate hunters still face
    // the same boss; a forager who blunders into the wallow gets time to understand
    // the warning and turn away.
    warningTicks: 30,
    flightAt: 10,
    // He keeps to the wallow and the wet ground around it, which is what makes him
    // a hunt for a *particular* animal you can find twice rather than a random
    // encounter. The escalation widens this when it arms him.
    homeRange: 10,
    browses: true,
    // He remembers you the better part of an hour. Break line of sight and he does
    // not simply forget and settle — he comes looking.
    memory: 400,
    // The one the Great Hunt is about: the escalation scheduler reads this to know
    // whose sign to start putting about, and his death is what ends the news.
    apex: true,
};
/**
 * A rabbit (M3-10). The animal the valley was missing.
 *
 * Everything about it is the opposite of the boar, and deliberately so: it is
 * everywhere, it is worth almost nothing, and it cannot hurt you. What it is for
 * is that a player who walks out of the village in any direction should meet
 * something living within a few minutes — and a valley whose only wildlife is
 * three wolf dens and a boar is a valley you can walk around all afternoon and
 * correctly meet nothing.
 *
 * `arc: 4` is not a typo. A rabbit's eyes are on the sides of its head and it
 * sees very nearly all the way round, so you cannot come at one from behind; what
 * you can do is come at it downwind, and the only reason anybody will ever learn
 * that lesson is that rabbits are cheap to practise on.
 */
export const RABBIT_INSTINCT = {
    ...DEFAULT_INSTINCT,
    id: 'rabbit',
    disposition: 'prey',
    senses: { sight: 8, arc: 4, smell: 5, upwindFactor: 2 },
    // It will fight only when `cornered` leaves it nothing else, and it will lose.
    nerve: 1,
    woundNerve: 3,
    packNerve: 0,
    oddsNerve: 1,
    closeAt: 1,
    flightAt: 6,
    // It does not go further from the warren than it can bolt back to. The tight
    // range is what makes a hedge a *place with rabbits in it*, which is in turn
    // what makes clearing one out something a player can notice having done.
    homeRange: 5,
    crepuscular: true,
    browses: true,
    // A short memory: get out of its sight for twenty seconds and it goes back to
    // feeding. It is the one animal in the valley you can genuinely stalk twice.
    memory: 30,
};
/* -------------------------------------------------------------------------- *
 * The village's own (M5-01)
 * -------------------------------------------------------------------------- */
/**
 * A cow, which is `docile` — the disposition M5-01 adds, and the only one in
 * the valley that describes an animal *bred out of fear*.
 *
 * Every number here is set against one scene: the wolves come over the fence at
 * night and the herders wake up to what is left. `flightAt: 2` is why that scene
 * is a slaughter rather than a chase — a cow does not leave when it sees a wolf
 * at eight tiles, it leaves when the wolf is on top of it, by which point
 * leaving is not a plan. `homeRange: 4` keeps it in the fold on its own without
 * a fence rule, which matters because the fence is scenery: what holds the herd
 * together is that none of them has ever wanted to be anywhere else.
 *
 * `nerve: 2` against `woundNerve: 2` means one real wound and it breaks. That is
 * not cowardice, it is mass without intent — the animal has no idea what to do
 * with a fight and never had.
 */
export const COW_INSTINCT = {
    ...DEFAULT_INSTINCT,
    id: 'cow',
    disposition: 'docile',
    // Eyes on the sides of the head, like the rabbit's, and a nose that is mostly
    // for grass. It will smell a wolf before it sees one and do nothing about it.
    senses: { sight: 7, arc: 3, smell: 6, upwindFactor: 2 },
    nerve: 2,
    woundNerve: 2,
    // Herd animals, and the herd is not a pack: a cow beside another cow is not
    // braver, it is simply not alone. Nothing here makes them fight together.
    packNerve: 0,
    oddsNerve: 0,
    closeAt: 1,
    flightAt: 2,
    homeRange: 4,
    crepuscular: false,
    browses: true,
    // It forgets you the moment you stop being in front of it. There is no
    // stalking a cow, and there is no point.
    memory: 20,
};
/** A ewe: the cow's numbers, smaller, and with even less of an opinion. */
export const SHEEP_INSTINCT = {
    ...COW_INSTINCT,
    id: 'sheep',
    senses: { sight: 6, arc: 3, smell: 5, upwindFactor: 2 },
    nerve: 1,
    woundNerve: 3,
    // It will move off a step sooner than a cow will, and that is the whole of the
    // difference in how the two die.
    flightAt: 3,
    homeRange: 3,
    memory: 15,
};
const INSTINCTS = [
    WOLF_INSTINCT,
    STAG_INSTINCT,
    BOAR_INSTINCT,
    YSGITHYRWYN_INSTINCT,
    RABBIT_INSTINCT,
    COW_INSTINCT,
    SHEEP_INSTINCT,
];
export const INSTINCT_KIT = new Map(INSTINCTS.map((one) => [one.id, one]));
export function instinct(id) {
    const found = INSTINCT_KIT.get(id);
    if (!found)
        throw new Error(`unknown instinct '${id}'`);
    return found;
}
/* -------------------------------------------------------------------------- *
 * What a den produces
 * -------------------------------------------------------------------------- */
function beastLoadout(planId, weaponId, armourIds, attributes, skill = 0) {
    return { planId, weaponId, armourIds, attributes: createAttributes(attributes), skill };
}
export const WOLF_BEAST = {
    id: 'wolf',
    name: 'a lean wolf',
    instinctId: 'wolf',
    loadout: beastLoadout('wolf', 'wolf.jaws', [], { frame: 10, agility: 13, toughness: 8 }, 
    // It has hunted every day of its life. Untrained is not the same as unskilled.
    1),
};
export const STAG_BEAST = {
    id: 'stag',
    name: 'a red stag',
    instinctId: 'stag',
    loadout: beastLoadout('stag', 'stag.antlers', ['stag.hide'], {
        frame: 13,
        agility: 14,
        toughness: 10,
    }),
};
export const BOAR_BEAST = {
    id: 'boar',
    name: 'a wild boar',
    instinctId: 'boar',
    loadout: beastLoadout('boar', 'boar.tusks', ['boar.hide'], {
        frame: 15,
        agility: 9,
        toughness: 14,
    }),
};
/**
 * Ysgithyrwyn himself (M5-04). The same shape as the wild boar's record, and
 * every number in it worse.
 *
 * The attributes match the harness fixture exactly — frame 20, toughness 20 —
 * because the fixture is what the README's win envelope is tuned against, and a
 * boar in the world that fought at different numbers than the boar in the batch
 * runner would make that tuning a lie. `skill: 2` is a lifetime of killing the
 * men who came before, which in this engine is the spearman's own training and a
 * different story.
 */
export const YSGITHYRWYN_BEAST = {
    id: 'ysgithyrwyn',
    name: 'Ysgithyrwyn',
    instinctId: 'ysgithyrwyn',
    loadout: beastLoadout('ysgithyrwyn', 'ysgithyrwyn.tusks', ['ysgithyrwyn.hide'], { frame: 20, agility: 8, toughness: 20 }, 2),
};
/**
 * Two kilos of it, and a frame of 2 — the smallest body the fight rules will
 * ever be handed.
 *
 * The frame is load-bearing beyond the fight: M3-10 scales sign strength off it,
 * so a rabbit's print is faint where a stag's is plain, and a novice tracker can
 * tell a trail worth following from one that is not without being told which.
 */
export const RABBIT_BEAST = {
    id: 'rabbit',
    name: 'a rabbit',
    instinctId: 'rabbit',
    loadout: beastLoadout('rabbit', 'rabbit.teeth', [], {
        frame: 2,
        agility: 15,
        toughness: 4,
    }),
};
/**
 * The village's cow (M5-01) — the single most valuable object in Cwm Twrch, and
 * it is standing in a field behind a fence made of sticks.
 *
 * A frame of 16 makes it the biggest body in the valley bar Ysgithyrwyn, and
 * that is deliberate: the smith's prices are quoted against this animal, so when
 * the wolves take it the loss has to be legible without anybody being told a
 * number. Toughness 12 and no skill at all — it has never fought and will not
 * learn tonight.
 */
export const COW_BEAST = {
    id: 'cow',
    name: 'the village cow',
    instinctId: 'cow',
    loadout: beastLoadout('cow', 'cow.horns', [], {
        frame: 16,
        agility: 6,
        toughness: 12,
    }),
};
/** A ewe. A hundred pounds of wool that has never had an idea. */
export const SHEEP_BEAST = {
    id: 'sheep',
    name: 'a ewe',
    instinctId: 'sheep',
    loadout: beastLoadout('sheep', 'sheep.butt', [], {
        frame: 6,
        agility: 8,
        toughness: 7,
    }),
};
const BEASTS = [
    WOLF_BEAST,
    STAG_BEAST,
    BOAR_BEAST,
    YSGITHYRWYN_BEAST,
    RABBIT_BEAST,
    COW_BEAST,
    SHEEP_BEAST,
];
export const BEAST_KIT = new Map(BEASTS.map((one) => [one.id, one]));
export function beast(id) {
    const found = BEAST_KIT.get(id);
    if (!found)
        throw new Error(`unknown beast '${id}'`);
    return found;
}
/* -------------------------------------------------------------------------- *
 * What comes off them (M3-06)
 * -------------------------------------------------------------------------- */
/**
 * Portions of meat a whole, fresh animal gives up, keyed by **body plan** —
 * which is what a `Carcass` records as its species.
 *
 * The spread is the point, and it is the reason the Great Hunt (M5) can be
 * about a boar rather than about a number: a wolf is barely a meal and a stag
 * is a week, so what you choose to hunt is a decision about how long you can
 * stay out. Two portions come off per trip to the block, so a stag is three
 * visits and a thing you camp beside.
 *
 * Wolf meat is on the list and it is thin, which is honest — people did eat it,
 * and nobody was pleased about it.
 *
 * `yields` (M5-01) is the other half and the half the economy runs on. Meat
 * spoils and skins do not, so what a hunter three days out actually gets home
 * with is the hide — which is why the smith's prices are quoted in hides and
 * why hunting further than an afternoon from the village pays at all. It comes
 * off whole on the first trip to the block, because you skin an animal once,
 * before you start cutting.
 */
export const BUTCHERY = new Map([
    ['wolf', { itemId: 'raw-meat', portions: 2, yields: [{ itemId: 'pelt', count: 1 }] }],
    ['stag', { itemId: 'raw-meat', portions: 6, yields: [{ itemId: 'hide', count: 1 }] }],
    [
        'boar',
        {
            itemId: 'raw-meat',
            portions: 5,
            // The tusks are the trophy and the payday both. They are also the reason a
            // boar is worth the risk of a boar, which is the whole of M5-03's pitch.
            yields: [
                { itemId: 'hide', count: 1 },
                { itemId: 'tusks', count: 1 },
            ],
        },
    ],
    [
        'ysgithyrwyn',
        {
            itemId: 'raw-meat',
            // More meat than the village cow, because he is bigger than the village
            // cow — a hunt that feeds Llan-y-Nant for a fortnight, which is half of
            // why it was worth the dead.
            portions: 10,
            // The hide the smith has never seen the like of, the tusks that are the
            // whole point — the ones that go up in the hall (M5-05) — and the three
            // old spearheads carried in his shoulders since before you were born, each
            // one a hunter who went up the ford and did not come back (M5-04). They
            // come off him at the block, the post-mortem extraction the ticket asks
            // for, and each is its own item because each is its own dead man.
            yields: [
                { itemId: 'hide', count: 1 },
                { itemId: 'tusks', count: 1 },
                { itemId: 'spearhead.cadog', count: 1 },
                { itemId: 'spearhead.maelan', count: 1 },
                { itemId: 'spearhead.unknown', count: 1 },
            ],
        },
    ],
    // One portion, which is the bottom of the scale and the reason the scale
    // exists: a rabbit is tonight, a stag is the week. What you choose to hunt is
    // a decision about how long you can stay out. Nothing comes off its back that
    // anybody would buy.
    ['rabbit', { itemId: 'raw-meat', portions: 1 }],
    // M5-01's livestock. A cow is more meat than anything wild in the valley and
    // that is exactly the temptation: butchering the village's cow is a week of
    // food and a thing the herders will still be talking about in the third week.
    ['cow', { itemId: 'raw-meat', portions: 8, yields: [{ itemId: 'hide', count: 1 }] }],
    ['sheep', { itemId: 'raw-meat', portions: 3, yields: [{ itemId: 'fleece', count: 1 }] }],
]);
export function butchery(speciesId) {
    return BUTCHERY.get(speciesId) ?? null;
}
//# sourceMappingURL=wildlife.js.map